import { FC, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import type { TBlockConfirmationDialogProps } from './types';
import {
	getBlockEmerchantLoading,
	getBlockEmerchantSuccess,
} from '../../redux/reducers/emManagement/selectors';
import {
	blockEmerchantRequest,
	resetBlockEmerchant,
} from '../../redux/reducers/emManagement/reducer';

export const BlockConfirmationDialog: FC<TBlockConfirmationDialogProps> = ({
	open,
	setClose,
	// handleConfirm,
	// title = 'Block client access',
	blockTargetName,
	emId,
}) => {
	const dispatch = useDispatch();
	const confirmWord = 'BLOCK';
	const confirmButtonText = 'Yes';
	const cancelButtonText = 'No';
	const [inputValue, setInputValue] = useState('');
	const blockEmerchantLoading = useSelector(getBlockEmerchantLoading);
	const blockEmerchantSuccess = useSelector(getBlockEmerchantSuccess);
	const [isConfirmAllowed, setIsConfirmAllowed] = useState(false);

	const handleConfirm = () => {
		dispatch(blockEmerchantRequest({ id: Number(emId) }));
	};

	useEffect(() => {
		if (!open) {
			setInputValue('');
		}
		if (
			open &&
			blockEmerchantSuccess &&
			!blockEmerchantLoading &&
			inputValue.trim() === confirmWord &&
			isConfirmAllowed
		) {
			dispatch(resetBlockEmerchant());
			setClose();
			setIsConfirmAllowed(false);
			setInputValue('');
		}
	}, [
		open,
		blockEmerchantSuccess,
		blockEmerchantLoading,
		inputValue,
		setClose,
		isConfirmAllowed,
		dispatch,
	]);

	// const isConfirmAllowed = inputValue.trim() === normalizedConfirmWord;

	const handleDialogClose = () => {
		setInputValue('');
		setIsConfirmAllowed(false);
		setClose();
	};

	const handleConfirmClick = () => {
		if (inputValue.trim() === confirmWord) {
			setIsConfirmAllowed(true);
			handleConfirm();
		} else {
			setIsConfirmAllowed(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleDialogClose}
			sx={{
				'& .MuiDialog-paper': {
					width: '520px',
					maxWidth: '520px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary">Block EM</DialogTitle>

			<DialogContent
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 2,

					position: 'relative',
				}}
			>
				{blockEmerchantLoading && (
					<>
						<Box
							component="div"
							sx={{
								backgroundColor: 'rgba(255, 255, 255, 0.7)',
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								zIndex: 1000,
								padding: 0,
								margin: 0,
							}}
						/>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								height: '100%',
								width: '100%',
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								zIndex: 1000,
								padding: 0,
								margin: 0,
							}}
						>
							<CircularProgress size={40} />
						</Box>
					</>
				)}

				<DialogContentText color="text.primary">
					<strong>{blockTargetName}</strong> will be blocked. <br />
					Access to dashboard and to EAS solution functionality will be revoked. Are you sure you
					want to block <strong>{blockTargetName}</strong> access?
				</DialogContentText>

				<DialogContentText color="text.primary">
					Please enter <strong>{confirmWord}</strong> in the text box below to proceed.
				</DialogContentText>
				<TextField
					autoFocus
					fullWidth
					autoComplete="off"
					label={`Type ${confirmWord}`}
					placeholder={confirmWord}
					value={inputValue}
					onChange={event => setInputValue(event.target.value)}
					variant="outlined"
					size="small"
				/>
			</DialogContent>
			<DialogActions>
				<Button
					variant="outlined"
					onClick={handleDialogClose}
					size="small"
					disabled={blockEmerchantLoading}
				>
					{cancelButtonText}
				</Button>
				<Button
					variant="contained"
					color="error"
					onClick={handleConfirmClick}
					disabled={inputValue.trim() !== confirmWord || blockEmerchantLoading}
					size="small"
				>
					{confirmButtonText}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
